import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../api';

const OBJECTIVES = [
  { value: 'traffic', label: 'Traffic', supported: true },
  { value: 'awareness', label: 'Awareness', supported: true },
  { value: 'engagement', label: 'Engagement', supported: true },
  { value: 'leads', label: 'Leads (coming soon)', supported: false },
  { value: 'conversions', label: 'Conversions (coming soon)', supported: false },
  { value: 'app_promotion', label: 'App Promotion (coming soon)', supported: false },
];

const initialForm = {
  client_id: '',
  brand: '',
  headline: '',
  idea_description: '',
  platform: 'meta',
  landing_page_url: '',
  objective: 'traffic',
  google_campaign_type: 'search',
  daily_budget: '',
  daily_budget_meta: '',
  daily_budget_google: '',
  lifetime_cap: '',
  target_cpa: '',
  target_roas: '',
  start_date: '',
  end_date: '',
  creative_source: 'uploaded',
  primary_text: '',
  ad_headline: '',
  cta: '',
  age_min: '',
  age_max: '',
  gender: 'all',
  location: 'India',
  interests_raw: '',
};

function FieldTag({ scope }) {
  const styles = {
    meta: 'bg-blue-50 text-blue-600',
    google: 'bg-yellow-50 text-yellow-700',
    both: 'bg-gray-100 text-gray-500',
  };
  const labels = { meta: 'Meta', google: 'Google', both: 'Both' };
  return (
    <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${styles[scope]}`}>
      {labels[scope]}
    </span>
  );
}

export default function NewAdIdea() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [suggestingKeywords, setSuggestingKeywords] = useState(false);
  const [keywordsExact, setKeywordsExact] = useState('');
  const [keywordsPhrase, setKeywordsPhrase] = useState('');
  const [keywordsNegative, setKeywordsNegative] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    api.getClients().then(setClients).catch((err) => setError(err.message));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSuggestKeywords = async () => {
    if (!form.landing_page_url || !form.brand) {
      setError('Fill in Brand and Landing Page URL first');
      return;
    }
    setSuggestingKeywords(true);
    setError('');
    try {
      const data = await api.suggestKeywords({
        landing_page_url: form.landing_page_url,
        brand: form.brand,
        idea_description: form.idea_description,
      });
      setKeywordsExact((data.exact_match || []).join(', '));
      setKeywordsPhrase((data.phrase_match || []).join(', '));
      setKeywordsNegative((data.negative || []).join(', '));
    } catch (err) {
      setError(err.message);
    } finally {
      setSuggestingKeywords(false);
    }
  };


  const buildCreativeCopy = () => {
    const parts = [];
    if (form.primary_text) parts.push(`Primary text:\n\n${form.primary_text}`);
    if (form.ad_headline) parts.push(`Headline: ${form.ad_headline}`);
    if (form.cta) parts.push(`CTA: ${form.cta}`);
    return parts.join('\n\n');
  };

  const buildFormData = (platform, dailyBudget) => {
    const fd = new FormData();
    fd.append('client_id', form.client_id);
    fd.append('brand', form.brand);
    fd.append('headline', form.headline);
    fd.append('idea_description', form.idea_description);
    fd.append('platform', platform);
    fd.append('objective', form.objective);
    fd.append('daily_budget', dailyBudget);
    fd.append('landing_page_url', form.landing_page_url);
    if (platform === 'google') fd.append('google_campaign_type', form.google_campaign_type);
    if (form.lifetime_cap) fd.append('lifetime_cap', form.lifetime_cap);
    if (form.target_cpa) fd.append('target_cpa', form.target_cpa);
    if (form.target_roas) fd.append('target_roas', form.target_roas);
    if (form.start_date) fd.append('start_date', form.start_date);
    if (form.end_date) fd.append('end_date', form.end_date);
    fd.append('creative_source', form.creative_source);
    fd.append('creative_copy', buildCreativeCopy());
    fd.append('targeting', JSON.stringify({
      age_min: form.age_min || null,
      age_max: form.age_max || null,
      gender: form.gender,
      location: form.location,
      interests_raw: form.interests_raw,
    }));
    fd.append('status', 'draft');
    if (file) fd.append('creative_file', file);
    if (platform === 'google') {
      fd.append('keywords_exact', keywordsExact);
      fd.append('keywords_phrase', keywordsPhrase);
      fd.append('keywords_negative', keywordsNegative);
    }
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (!form.landing_page_url) {
        throw new Error('Landing Page URL is required');
      }
      if (form.platform === 'both') {
        if (!form.daily_budget_meta || !form.daily_budget_google) {
          throw new Error('Enter a daily budget for both Meta and Google');
        }
        await api.createAd(buildFormData('meta', form.daily_budget_meta));
        await api.createAd(buildFormData('google', form.daily_budget_google));
      } else {
        if (!form.daily_budget) {
          throw new Error('Enter a daily budget');
        }
        await api.createAd(buildFormData(form.platform, form.daily_budget));
      }
      setSuccess(true);
      setForm(initialForm);
      setFile(null);
      setTimeout(() => navigate('/ads/status'), 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const totalBudget =
    (parseFloat(form.daily_budget_meta) || 0) + (parseFloat(form.daily_budget_google) || 0);

  const showMeta = form.platform === 'meta' || form.platform === 'both';
  const showGoogle = form.platform === 'google' || form.platform === 'both';

  return (
    <Layout>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">New Ad Idea</h1>

      {error && <div className="bg-red-50 text-red-600 text-sm rounded-lg px-3 py-2 mb-4">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 text-sm rounded-lg px-3 py-2 mb-4">Saved as draft.</div>}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow p-6 grid grid-cols-2 gap-4 max-w-3xl">

        {/* Client */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
          <select name="client_id" value={form.client_id} onChange={handleChange} required
            className="w-full border border-gray-300 rounded-lg px-3 py-2">
            <option value="">Select a client...</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.client_name}</option>
            ))}
          </select>
        </div>

        {/* Brand + Headline */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Brand *</label>
          <input name="brand" value={form.brand} onChange={handleChange} required
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Headline / Internal Ref *</label>
          <input name="headline" value={form.headline} onChange={handleChange} required
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        {/* Idea Description - moved up */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Idea Description</label>
          <textarea name="idea_description" value={form.idea_description} onChange={handleChange} rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        {/* Platform */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Platform *</label>
          <div className="flex gap-4 mt-2">
            {[
              { value: 'meta', label: 'Meta' },
              { value: 'google', label: 'Google' },
              { value: 'both', label: 'Both (creates 2 records)' },
            ].map((p) => (
              <label key={p.value} className="flex items-center gap-2 text-sm">
                <input type="radio" name="platform" value={p.value} checked={form.platform === p.value} onChange={handleChange} />
                {p.label}
              </label>
            ))}
          </div>
        </div>

        {/* Landing Page URL - moved up, right after platform */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Landing Page URL * <FieldTag scope="both" />
          </label>
          <input type="url" name="landing_page_url" value={form.landing_page_url} onChange={handleChange} required
            placeholder="https://chatore.shahiraj.com"
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        {/* Objective */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Objective * <FieldTag scope="both" />
          </label>
          <select name="objective" value={form.objective} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2">
            {OBJECTIVES.map((o) => <option key={o.value} value={o.value} disabled={!o.supported}>{o.label}</option>)}
          </select>
        </div>

        {showGoogle && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Google Campaign Type <FieldTag scope="google" />
            </label>
            <select name="google_campaign_type" value={form.google_campaign_type} onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2">
              <option value="search">Search</option>
              <option value="pmax">Performance Max</option>
            </select>
          </div>
        )}

        {showGoogle && (
          <div className="col-span-2 border-t border-gray-100 pt-4 mt-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Google Keywords</h3>
              <button type="button" onClick={handleSuggestKeywords} disabled={suggestingKeywords}
                className="text-xs bg-brand-purple text-white px-3 py-1.5 rounded-lg hover:bg-brand-purpleDark disabled:opacity-50">
                {suggestingKeywords ? 'Thinking...' : '✨ Suggest Keywords'}
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Exact Match</label>
                <textarea value={keywordsExact} onChange={(e) => setKeywordsExact(e.target.value)} rows={2}
                  placeholder="Click Suggest Keywords, or type comma-separated phrases"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phrase Match</label>
                <textarea value={keywordsPhrase} onChange={(e) => setKeywordsPhrase(e.target.value)} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Negative Keywords</label>
                <textarea value={keywordsNegative} onChange={(e) => setKeywordsNegative(e.target.value)} rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          </div>
        )}

        {/* Budget */}
        {form.platform === 'both' ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meta Daily Budget (₹) * <FieldTag scope="meta" />
              </label>
              <input type="number" step="0.01" name="daily_budget_meta" value={form.daily_budget_meta} onChange={handleChange} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              <p className="text-xs text-gray-400 mt-1">Meta requires ₹96+ for most objectives; ₹300+ recommended for reliable delivery.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Google Daily Budget (₹) * <FieldTag scope="google" />
              </label>
              <input type="number" step="0.01" name="daily_budget_google" value={form.daily_budget_google} onChange={handleChange} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div className="col-span-2 bg-brand-hero rounded-lg px-4 py-2 text-sm text-brand-purple font-medium">
              Total Daily Budget: ₹{totalBudget.toFixed(2)}
            </div>
          </>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Daily Budget (₹) *</label>
            <input type="number" step="0.01" name="daily_budget" value={form.daily_budget} onChange={handleChange} required
              className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            {showMeta && (
              <p className="text-xs text-gray-400 mt-1">Meta requires ₹96+ for most objectives; ₹300+ recommended for reliable delivery.</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Lifetime Cap (₹)</label>
          <input type="number" step="0.01" name="lifetime_cap" value={form.lifetime_cap} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target CPA (₹)</label>
          <input type="number" step="0.01" name="target_cpa" value={form.target_cpa} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target ROAS</label>
          <input type="number" step="0.01" name="target_roas" value={form.target_roas} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input type="date" name="start_date" value={form.start_date} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input type="date" name="end_date" value={form.end_date} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        {/* Targeting */}
        <div className="col-span-2 border-t border-gray-100 pt-4 mt-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Target Audience (optional)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Age Min</label>
              <input type="number" name="age_min" value={form.age_min} onChange={handleChange} min="13" max="65"
                className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Age Max</label>
              <input type="number" name="age_max" value={form.age_max} onChange={handleChange} min="13" max="65"
                className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select name="gender" value={form.gender} onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2">
                <option value="all">All</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input name="location" value={form.location} onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              <p className="text-xs text-gray-400 mt-1">Currently India-only supported for launch.</p>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Interests / Audience Description</label>
              <textarea name="interests_raw" value={form.interests_raw} onChange={handleChange} rows={2}
                placeholder="e.g. small business owners, WhatsApp marketing, e-commerce tools, healthcare clinics"
                className="w-full border border-gray-300 rounded-lg px-3 py-2" />
              <p className="text-xs text-gray-400 mt-1">Type freely — matched to platform targeting automatically. (Not applied to launch yet — coming in a future update.)</p>
            </div>
          </div>
        </div>

        {/* Creative */}
        <div className="col-span-2 border-t border-gray-100 pt-4 mt-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Creative</h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Creative Source</label>
          <select name="creative_source" value={form.creative_source} onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2">
            <option value="uploaded">I'll upload it</option>
            <option value="ai_generated">Generate with AI</option>
          </select>
        </div>

        {form.creative_source === 'uploaded' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Creative File <FieldTag scope="meta" />
            </label>
            <input type="file" accept="image/*,video/*" onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-sm" />
            <p className="text-xs text-gray-400 mt-1">Used by Meta. Google Search ads are text-only and don't use this file.</p>
          </div>
        )}

        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Primary Text * <FieldTag scope="both" />
          </label>
          <textarea name="primary_text" value={form.primary_text} onChange={handleChange} rows={3}
            placeholder="The main ad copy — used as Meta's primary text and Google's description."
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Headline (short) * <FieldTag scope="both" />
          </label>
          <input name="ad_headline" value={form.ad_headline} onChange={handleChange}
            placeholder="e.g. WhatsApp Automation for Indian Businesses"
            maxLength={60}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          <p className="text-xs text-gray-400 mt-1">Keep under 30 characters for best Google Search display.</p>
        </div>

        {showMeta && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CTA <FieldTag scope="meta" />
            </label>
            <input name="cta" value={form.cta} onChange={handleChange}
              placeholder="e.g. Learn More, Get Started"
              className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            <p className="text-xs text-gray-400 mt-1">Google Search ads auto-derive their call-to-action; this field is used for Meta only.</p>
          </div>
        )}

        <div className="col-span-2">
          <button type="submit" disabled={submitting}
            className="bg-brand-purple text-white px-6 py-2 rounded-lg font-medium hover:bg-brand-purpleDark disabled:opacity-50">
            {submitting ? 'Saving...' : form.platform === 'both' ? 'Save 2 Drafts (Meta + Google)' : 'Save as Draft'}
          </button>
        </div>
      </form>
    </Layout>
  );
}
